<script lang="ts">
import { ref, watch, onMounted, onBeforeMount, onBeforeUnmount, onUpdated, computed } from 'vue';
import GeoIPOverlay1XP from '../GeoIPOverlay1XP/GeoIPOverlay1XP.vue';
import SkipLink from './components/SkipLink/SkipLink.vue';
import Logo from '../Logo/Logo.vue';
import PageHeaderSearch from '../PageHeaderSearch/PageHeaderSearch.vue';
import ExternalLinks from './components/ExternalLinks/ExternalLinks.vue';
import CountryLanguageSwitcher1XP from '../CountryLanguageSwitcher1XP/CountryLanguageSwitcher1XP.vue';
import CustomerNavigation1XP from '../CustomerNavigation1XP/CustomerNavigation1XP.vue';
import ShopCart1XP from '../ShopCart1XP/ShopCart1XP.vue';
import ToggleButton from './components/ToggleButton/ToggleButton.vue';
import MainNav from '../MainNav/MainNav.vue';
import Meta from './components/Meta/Meta.vue';
import { useHeaderStore } from '../../store/headerState/headerState.ts';
import { useStoreConfiguration } from '../../store/configuration/configuration.ts';
import { useStoreNavContent } from '../../store/content/navContent.ts';
import { useLogoStore } from '../../store/headerState/logoState.ts';
import { useStoreMainNavigation } from '../../store/navigation/mainNavSingle.ts';
import { useStoreMetaNavigation } from '../../store/navigation/metaNavSingle.ts';
import { useStoreSecondaryNav } from '../../store/navigation/secondaryNavSingle.ts';
import { useStoreTranslations } from '../../store/translations/translations.ts';
import type { Instance, WindowWika } from '../../types/window.ts';
import {
  isMobile,
  isTablet,
  isDesktop,
  isWidescreen,
  isDesktopOrWidescreen,
  isTabletOrMobile,
} from '../../utils/breakpoints.js';
import type {
  StoreConfiguration,
  StoreCustomerConfig,
  StoreShopConfig,
} from '../../types/storeConfig.ts';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher.vue';
</script>
<script setup lang="ts">
const headerStore = useHeaderStore();
const mainNav = useStoreMainNavigation();
const metaNav = useStoreMetaNavigation();
const secondaryNav = useStoreSecondaryNav();
const configStore = useStoreConfiguration();
const configLogo = useLogoStore();
const navContentTeasers = useStoreNavContent();
const translations = useStoreTranslations();

const isHeaderOpen = ref(false);
const activeNodes = ref<string[]>([]);
const context = ref<Instance | undefined>(configStore.caasConfig?.instance);
const config = ref<StoreConfiguration | undefined>(configStore.config);
const caasConfig = ref<WindowWika['_CAAS_config'] | undefined>(configStore.caasConfig);
const configCustomer = ref<StoreCustomerConfig | undefined>(configStore.configCustomer);
const configShop = ref<StoreShopConfig | undefined>(configStore.configShop);
const scrollPositionRecord = ref<number>(document.body.getBoundingClientRect().top);
const currentScrollPosition = ref<number>(0);
const isScrollingUp = ref<boolean>(false);
const isScrollingDown = ref<boolean>(false);
const isHeaderOffscreen = ref<boolean>(false);
const headerRef = ref();
const headerHeight = ref<number>(0);
const scrollBarWidth = ref<number>(0);
const micrositeTitle = ref<string | undefined>(configStore.caasConfig?.websiteTitle);
const isUserLoggedIn = ref<boolean>(false);
const currentCountry = ref<string | undefined>(configStore.config?.caas_restServiceShopCountry);
const projectIsCorporateEN = ref<boolean | undefined>(
  configStore.config?.ps_project_type === 'corporateEN',
);

const contextMicrosite = computed(() => {
  if (context.value && context.value.includes('microsite')) {
    return true;
  } else {
    return false;
  }
});

const handleNavClick = () => {
  headerStore.setIsHeaderOpen(!isHeaderOpen.value);
};

const findActiveNodes = (currentPage) => {
  activeNodes.value = [];
  const firstLevelNav = mainNav.items && mainNav.items[0]?.children;
  const activeNode = currentPage;
  let activeMainLink: string | undefined;
  let activeNodeParent: string | undefined;
  let activeNodeParentIsOnSubLevel1: string | undefined;
  let activeNodeParentIsOnSubLevel2: string | undefined;
  let activeNodeParentIsOnSubLevel3: string | undefined;

  activeMainLink =
    mainNav.items &&
    mainNav.items[0]?.children &&
    mainNav.items[0]?.children.find((item) => item._id === activeNode)?._id;

  if (!activeMainLink && firstLevelNav?.length) {
    activeNodes.value.push(activeNode);

    for (const item of firstLevelNav) {
      if (item.children) {
        for (const child of item.children) {
          if (child.children) {
            for (const grandChild of child.children) {
              const activePageIsOnLevel4 = grandChild.children?.some(
                (grandGrandChild) => grandGrandChild._id === activeNode,
              );
              if (activePageIsOnLevel4) {
                activeNodeParent = grandChild._id;
              }

              activeNodeParent && activeNodes.value.unshift(activeNodeParent);
            }
            if (activeNodes.value?.length === 2) {
              activeNodeParentIsOnSubLevel3 =
                child.children.find((grandChild) => grandChild._id === activeNodes.value[0]) &&
                child._id;
              activeNodeParentIsOnSubLevel3 &&
                activeNodes.value.unshift(activeNodeParentIsOnSubLevel3);
            }
          }

          const activePageIsOnLevel3 = child.children?.some(
            (grandChild) => grandChild._id === activeNode,
          );
          if (activePageIsOnLevel3) {
            activeNodeParent = child._id;
          }

          activeNodeParent && activeNodes.value.unshift(activeNodeParent);

          if (activeNodes.value?.length === 2) {
            activeNodeParentIsOnSubLevel2 =
              item.children.find((child) => child._id === activeNodes.value[0]) && item._id;
            activeNodeParentIsOnSubLevel2 &&
              activeNodes.value.unshift(activeNodeParentIsOnSubLevel2);
          }
        }

        activeNodeParentIsOnSubLevel1 =
          item.children.find((child) => child._id === activeNodes.value[0]) && item._id;
        activeNodeParentIsOnSubLevel1 && activeNodes.value.unshift(activeNodeParentIsOnSubLevel1);
      }
    }
  } else {
    activeMainLink && activeNodes.value.push(activeMainLink);
  }
};

const setBodyPadding = () => {
  const bodyTag = document.body;

  if (headerHeight.value && !isScrollingDown.value) {
    bodyTag.style.paddingTop = `${headerHeight.value}px`;
  }
};

const shouldHeaderBeSticky = () => {
  const bodyTag = document.body;

  if (Math.abs(scrollPositionRecord.value) <= headerHeight.value) {
    scrollPositionRecord.value = bodyTag.getBoundingClientRect().top;
    return;
  }

  currentScrollPosition.value = bodyTag.getBoundingClientRect().top;
  isScrollingUp.value =
    Math.abs(currentScrollPosition.value) < Math.abs(scrollPositionRecord.value);
  isScrollingDown.value =
    Math.abs(currentScrollPosition.value) > Math.abs(scrollPositionRecord.value);

  if (isScrollingDown.value === isScrollingUp.value) {
    return;
  }

  scrollPositionRecord.value = bodyTag.getBoundingClientRect().top;
  return;
};

const initBackButton = () => {
  const backButtonContainer = document.querySelector('.historyBack');

  if (!backButtonContainer) {
    return;
  }

  const referrer = document.referrer;
  const currentDomain = window.location.hostname;

  if (referrer && referrer.includes(currentDomain)) {
    (backButtonContainer as HTMLElement).style.display = 'flex';
  }
};

initBackButton();

watch(
  () => headerStore.isHeaderOpen,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      isHeaderOpen.value = newValue;

      if (newValue) {
        document.body.classList.add('noScroll');
      } else {
        if (document.body.classList.contains('noScroll')) {
          document.body.classList.remove('noScroll');
        }
      }
    }
  },
);

watch(headerRef, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    headerHeight.value = newValue.getBoundingClientRect().height;
    setBodyPadding();
  }
});

watch(headerHeight, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    setBodyPadding();
  }
});

watch(currentScrollPosition, (newValue) => {
  if (isScrollingDown.value && Math.abs(newValue) >= headerHeight.value) {
    isHeaderOffscreen.value = true;
  }

  if (isScrollingUp.value) {
    isHeaderOffscreen.value = false;
  }
});

watch(
  configStore,
  (newValue) => {
    if (newValue) {
      config.value = newValue.config;
      caasConfig.value = newValue.caasConfig;
      configCustomer.value = newValue.configCustomer;
      configShop.value = newValue.configShop;
    }
  },
  { immediate: true },
);

watch(
  config,
  (newValue, oldValue) => {
    if (oldValue !== newValue && newValue) {
      projectIsCorporateEN.value = newValue.ps_project_type === 'corporateEN';
      currentCountry.value = newValue.caas_restServiceShopCountry;
    }
  },
  { immediate: true },
);

const setScrollbarWidth = () => {
  const width = window.innerWidth - document.documentElement.clientWidth;

  if (width !== scrollBarWidth.value) {
    document.documentElement.style.setProperty('--scrollbar-width', `${width}px`);
    scrollBarWidth.value = width;
  }
};

onBeforeMount(() => {
  window.addEventListener('ready', () => setBodyPadding());
  window.addEventListener('scroll', () => {
    if (!isHeaderOpen.value) {
      shouldHeaderBeSticky();
    }
  });
  window.addEventListener('resize', setScrollbarWidth);
  setScrollbarWidth();
});

onMounted(async () => {
  await configStore.init();

  configStore.caasConfig?.currentPageUUID &&
    findActiveNodes(configStore.caasConfig.currentPageUUID);
  window.dispatchEvent(new CustomEvent('headerMounted'));
});

onUpdated(() => {
  if (headerRef.value) {
    headerHeight.value = headerRef.value.getBoundingClientRect().height;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('ready', () => setBodyPadding());
  window.removeEventListener('scroll', () => {
    if (!isHeaderOpen.value) {
      shouldHeaderBeSticky();
    }
  });
  window.addEventListener('resize', setScrollbarWidth);
});
</script>

<template>
  <header
    ref="headerRef"
    :class="[
      $style.headerContainer,
      isHeaderOpen && $style.navOpen,
      isScrollingUp && scrollPositionRecord !== 0 ? $style.scrollIntoView : null,
      isHeaderOffscreen && scrollPositionRecord !== 0 ? $style.headerOffscreen : null,
    ]"
  >
    <GeoIPOverlay1XP
      v-if="configStore && caasConfig && config"
      :corporate-shop-url="config.pt_corporate_shop_page"
      :corporate-website-url="config.pt_corporate_page"
      :geo-ip-service-url="config.pg_geo_service_url"
      :instance="context || 'website'"
      :is-corporate-website="config.caas_isCorporateWebsite"
      :website-country="config.caas_countryCode"
      @change="
        () => {
          headerHeight = headerRef?.getBoundingClientRect().height;
          setBodyPadding();
        }
      "
    />
    <SkipLink />
    <section :class="$style.upper">
      <template v-if="contextMicrosite">
        <div :class="$style.upperHeader">
          <Logo
            :base-url="configStore.caasUrl"
            :image-config="configLogo.imageConfig"
            variant="original"
          />
          <h1 v-if="micrositeTitle">{{ micrositeTitle }}</h1>
        </div>
      </template>
      <template v-else>
        <Logo
          :base-url="configStore.caasUrl"
          :image-config="configLogo.imageConfig"
          variant="original"
        />
      </template>
      <div :class="$style.searchContainer">
        <PageHeaderSearch
          v-if="config?.caas_headerSearch && (isDesktop || isWidescreen)"
          :placeholder="
            context === 'shop'
              ? translations.translations?.['shop.search.placeholder']
              : translations.translations?.['search.placeholder']
          "
        />
      </div>
      <div v-if="metaNav && (isDesktop || isWidescreen)" :class="$style.externalLinks">
        <ExternalLinks v-if="metaNav.items" :items="metaNav.items" />
      </div>
      <LanguageSwitcher
        v-if="
          isDesktopOrWidescreen &&
          caasConfig &&
          caasConfig.availableLanguages &&
          caasConfig.availableLanguages.length > 0 &&
          (context === 'microsite_hr' || context === 'microsite-hr' || context === 'microsite')
        "
        :options="caasConfig.availableLanguages"
        :selected-language="caasConfig.language"
      />
      <CountryLanguageSwitcher1XP
        v-if="
          isDesktopOrWidescreen &&
          config &&
          caasConfig &&
          context !== 'microsite_hr' &&
          context !== 'microsite-hr' &&
          context !== 'microsite'
        "
        :corporate-url="
          context === 'shop' ? config.pt_corporate_shop_page : config.pt_corporate_page
        "
        :current-country="config.caas_countryLabel"
        :current-language="caasConfig.language"
        :current-language-code="caasConfig.languageIso3"
        :instance="context || 'website'"
        :is-corporate-e-n="projectIsCorporateEN"
      />
      <PageHeaderSearch
        v-if="config?.caas_headerSearch && !isDesktop && !isWidescreen"
        placeholder="Search within WIKA"
      />
      <CustomerNavigation1XP
        v-if="context == 'shop' && configCustomer"
        :config="configCustomer"
        @user-logged-in="(emittedData) => (isUserLoggedIn = emittedData)"
      />
      <ShopCart1XP
        v-if="context == 'shop' && configShop && caasConfig && configCustomer && currentCountry"
        :config="configShop"
        :current-country="currentCountry"
        :current-language="caasConfig.language"
        :i-frame-page="configCustomer.iFramePage"
        :is-user-logged-in="isUserLoggedIn"
      />
      <ToggleButton
        aria-controls="lowerSection"
        :aria-expanded="isHeaderOpen"
        :is-active="isHeaderOpen"
        @click="handleNavClick"
      />
    </section>
    <section :class="$style.lower">
      <template v-if="contextMicrosite">
        <div v-if="isMobile || isTablet" :class="$style.lowerHeader">
          <Logo
            v-if="configStore.caasUrl && configLogo.imageConfig"
            :base-url="configStore.caasUrl"
            :image-config="configLogo.imageConfig"
            variant="original"
          />
          <h1 v-if="micrositeTitle">{{ micrositeTitle }}</h1>
        </div>
      </template>
      <template v-else>
        <Logo
          v-if="configStore.caasUrl && configLogo.imageConfig && (isMobile || isTablet)"
          :base-url="configStore.caasUrl"
          :image-config="configLogo.imageConfig"
          variant="original"
        />
      </template>
      <MainNav
        v-if="mainNav && mainNav.items && mainNav.items[0]?.children && configStore.caasUrl"
        :active-nodes="activeNodes"
        :items="mainNav?.items && mainNav.items[0]?.children"
        :label="mainNav?.items && mainNav.items[0]?.label"
        :teasers="navContentTeasers?.items && navContentTeasers.items"
        @open-header="(e) => headerStore.setIsHeaderOpen(e)"
      />
      <div v-if="(isMobile || isTablet) && metaNav" :class="$style.externalLinks">
        <ExternalLinks v-if="metaNav.items" :items="metaNav.items" />
      </div>
      <LanguageSwitcher
        v-if="
          isTabletOrMobile &&
          caasConfig &&
          caasConfig.availableLanguages &&
          caasConfig.availableLanguages.length > 0 &&
          (context === 'microsite_hr' || context === 'microsite-hr' || context === 'microsite')
        "
        :options="caasConfig.availableLanguages"
        :selected-language="caasConfig.language"
      />
      <CountryLanguageSwitcher1XP
        v-if="
          isTabletOrMobile &&
          config &&
          config &&
          caasConfig &&
          context !== 'microsite_hr' &&
          context !== 'microsite-hr' &&
          context !== 'microsite'
        "
        :corporate-url="
          context === 'shop' ? config.pt_corporate_shop_page : config.pt_corporate_page
        "
        :current-country="config.caas_countryLabel"
        :current-language="caasConfig.language"
        :current-language-code="caasConfig.languageIso3"
        :instance="context || 'website'"
        :is-corporate-e-n="projectIsCorporateEN"
      />
      <Meta v-if="secondaryNav.items" :items="secondaryNav.items" />
    </section>
  </header>
</template>

<style module lang="scss">
@forward './PageHeader1XP.scss';
</style>
